import React from "react";
import { FaClock, FaUsers, FaCertificate, FaBook } from "react-icons/fa";

const Training = () => {
  const courses = [
    {
      id: 1,
      title: "Computer Networking",
      description:
        "Master the fundamentals of computer networking including TCP/IP, routing, switching, and network security. This comprehensive course covers network protocols, architecture, and troubleshooting techniques essential for any cybersecurity professional.",
      duration: "8 weeks",
      level: "Beginner to Intermediate",
      modules: 12,
      students: "500+",
      certificate: "Yes",
      highlights: [
        "OSI and TCP/IP models",
        "Network protocols and services",
        "Routing and switching concepts",
        "Network security fundamentals",
        "Hands-on lab exercises",
        "Real-world case studies",
      ],
      icon: "🌐",
    },
    {
      id: 2,
      title: "Ethical Hacking ",
      description:
        "Learn ethical hacking methodologies and penetration testing techniques used by cybersecurity professionals. Gain hands-on experience with industry-standard tools and methodologies to identify and exploit vulnerabilities responsibly.",
      duration: "12 weeks",
      level: "Intermediate to Advanced",
      modules: 16,
      students: "750+",
      certificate: "Yes",
      highlights: [
        "Reconnaissance and information gathering",
        "Vulnerability assessment techniques",
        "Web application security testing",
        "Network penetration testing",
        "Social engineering awareness",
        "Report writing and remediation",
      ],
      icon: "🔓",
    },
    {
      id: 3,
      title: "Cyber Security ",
      description:
        "Comprehensive cybersecurity training covering threat detection, incident response, and security operations. Perfect for those looking to start a career in cybersecurity or enhance their existing security knowledge.",
      duration: "10 weeks",
      level: "Beginner to Intermediate",
      modules: 14,
      students: "600+",
      certificate: "Yes",
      highlights: [
        "Threat landscape and attack vectors",
        "Security monitoring and SIEM tools",
        "Incident response procedures",
        "Digital forensics basics",
        "Risk assessment and management",
        "Compliance and regulatory frameworks",
      ],
      icon: "🛡️",
    },
  ];

  return (
    <div className="pt-24 bg-black text-white min-h-screen">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-6">
            Cybersecurity Training Programs
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Advance your career with industry-leading cybersecurity training.
            Our comprehensive courses are designed by experts and delivered with
            hands-on practical experience.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mt-12">
            <div className="text-center">
              <div className="text-3xl mb-4">🎓</div>
              <h3 className="text-lg font-semibold mb-2">Expert Instructors</h3>
              <p className="text-gray-400">
                Learn from industry professionals with real-world experience
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-4">💻</div>
              <h3 className="text-lg font-semibold mb-2">Hands-on Learning</h3>
              <p className="text-gray-400">
                Practical labs and real-world scenarios
              </p>
            </div>
            <div className="text-center">
              <div className="text-3xl mb-4">📜</div>
              <h3 className="text-lg font-semibold mb-2">Certification</h3>
              <p className="text-gray-400">
                Industry-recognized certificates upon completion
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold mb-4">
              Our Training Courses
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Choose from our carefully crafted curriculum designed to build
              your cybersecurity expertise from the ground up.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course, index) => (
              <div
                key={course.id}
                className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden hover:border-gray-600 transition-all duration-300"
              >
                {/* Course Header */}
                <div className="border-b border-gray-800 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl">{course.icon}</span>
                    <span className="text-sm text-gray-400 bg-gray-800 px-3 py-1 rounded">
                      Course {course.id}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {course.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{course.level}</p>
                </div>

                {/* Course Content */}
                <div className="p-6">
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {course.description}
                  </p>

                  {/* Course Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="text-center p-3 bg-gray-800 rounded">
                      <FaClock className="text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="font-semibold text-sm text-white">
                        {course.duration}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded">
                      <FaBook className="text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Modules</p>
                      <p className="font-semibold text-sm text-white">
                        {course.modules}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded">
                      <FaUsers className="text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Students</p>
                      <p className="font-semibold text-sm text-white">
                        {course.students}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-gray-800 rounded">
                      <FaCertificate className="text-gray-400 mx-auto mb-1" />
                      <p className="text-xs text-gray-500">Certificate</p>
                      <p className="font-semibold text-sm text-white">
                        {course.certificate}
                      </p>
                    </div>
                  </div>

                  {/* Course Highlights */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-white">
                      What You'll Learn
                    </h4>
                    <ul className="space-y-2">
                      {course.highlights.slice(0, 4).map((highlight, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <span className="text-gray-400 mr-2 mt-1">•</span>
                          <span className="text-gray-300">{highlight}</span>
                        </li>
                      ))}
                      {course.highlights.length > 4 && (
                        <li className="text-sm text-gray-500 pt-1">
                          +{course.highlights.length - 4} more topics
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <button className="w-full px-6 py-3 bg-white text-black font-semibold rounded transition duration-300 hover:bg-gray-100">
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Training;
